import { createStore, createEffect, createEvent, combine } from 'effector';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  CardKind,
  CardsApi,
  RequestStrategyEnum,
  type CreditLimitsResponse,
  type DebitLimitResponse,
  type UpdateCreditLimitRequest,
  type UpdateDebitLimitRequest,
  type WithSignal,
} from '@shared/api';

const getLimitEffect = createEffect(({ signal, ...request }: WithSignal<{ id: number; cardKind: CardKind }>) =>
  request.cardKind === CardKind.Credit ? CardsApi.getCreditLimit(request.id, signal) : CardsApi.getDebitLimit(request.id, signal),
);
const updateCreditLimitEffect = createEffect((request: UpdateCreditLimitRequest) => CardsApi.updateCreditLimit(request));
const updateDebitLimitEffect = createEffect((request: UpdateDebitLimitRequest) => CardsApi.updateDebitLimit(request));
const clearEvent = createEvent();

const $dataStore = createStore<CreditLimitsResponse | DebitLimitResponse | null>(null)
  .on(getLimitEffect.done, (current, data) => data.result.data || current)
  .on(updateCreditLimitEffect.done, (current, data) => data.result.data || current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getLimitEffect.pending, (_, data) => data)
  .on(getLimitEffect.fail, (_, data) => ApiUtilsV2.abortApiLoadingFailCaseTrigger(data.error))
  .on(getLimitEffect.done, () => false)
  .reset(clearEvent);
const $actionLoadingStore = createStore<boolean>(false)
  .on(updateCreditLimitEffect.pending, (_, data) => data)
  .on(updateCreditLimitEffect.fail, () => false)
  .on(updateCreditLimitEffect.done, () => false)
  .on(updateDebitLimitEffect.pending, (_, data) => data)
  .on(updateDebitLimitEffect.fail, () => false)
  .on(updateDebitLimitEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getLimitEffect, () => null)
  // .on(getCardLimitsEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getLimitEffect.fail, (_, data) =>
    ApiUtilsV2.abortApiErrorFailCaseTrigger(data.error, 'Something went wrong while fetching Card Limits. Please try again.'),
  )
  .on(getLimitEffect.done, () => null)
  .on(updateCreditLimitEffect, () => null)
  // .on(updateLimitCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateCreditLimitEffect.fail, () => 'Something went wrong while updating Card Limits. Please try again.')
  .on(updateCreditLimitEffect.done, () => null)
  .on(updateDebitLimitEffect, () => null)
  // .on(updateLimitCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateDebitLimitEffect.fail, () => 'Something went wrong while updating Card Limits. Please try again.')
  .on(updateDebitLimitEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((error) => $notificationStore.events.setError(error));

const getCardLimitsApi = ApiUtilsV2.wrapperApi(getLimitEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getCardLimitsApi.cancel());

export const $cardLimitStore = {
  events: {
    getLimit: getCardLimitsApi.trigger,
    updateCreditLimit: updateCreditLimitEffect,
    updateDebitLimit: updateDebitLimitEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    actionLoading: $actionLoadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
